/**
 * bootShell — universal boot used by runner + standalones.
 *
 * Phase 7: dispatchUrl and wireGlobalListeners stubs removed.
 * The caller (workspace/packages/workspace/index.js or a standalone
 * app's index.js) is responsible for calling dispatchUrl + wireGlobalListeners
 * AFTER bootShell returns the shell element.
 *
 * Pattern A (single-app standalones): when `apps` has exactly one entry and
 * no pre-built `context` is supplied, bootShell resolves the manifest's `app`
 * and `context` factories, mounts the shell, then auto-dispatches the current
 * URL and wires popstate. Standalone wrappers stay tiny — one bootShell call,
 * no extra imports. Multi-app shells (workspace/index.js) continue to call
 * dispatchUrl + wireGlobalListeners explicitly after bootShell returns.
 *
 * Sequence handled here:
 *  1. Early lang/theme from localStorage
 *  2. (Polyglot init — caller responsibility before bootShell)
 *  3. (ensureSDKInit — caller responsibility, or pass pre-init sdk)
 *  4. registerSdkAdapter({ sdk })
 *  5. Build/merge context state
 *  6. Auth gate via sdk.auth.getMe() — unauthed → /login, returns null
 *  7. Eager USER + ORG + WORKSPACE + WORKSPACE_PROJECT scope fetch
 *  8. Mount shell via create(app, context) — returns shell element
 *  9. [single-app only] dispatchUrl(shell, path) — initial route
 * 10. [single-app only] wireGlobalListeners(shell, ...) — popstate + storage
 *
 * Multi-app callers then:
 *  9. dispatchUrl(shell, path) — initial route (workspace/dispatch.js)
 * 10. wireGlobalListeners(shell, ...) — popstate + storage (workspace/wireGlobalListeners.js)
 */

import { registerSdkAdapter } from './sdkAdapter.js'
import { getSDK, awaitSDKInit } from './sdk.js'
import { supabaseRealtimeProvider } from './sdkRealtimeProvider.js'
import { diagnoseAuth, runAuthDiagnostic } from './diagnoseAuth.js'

/**
 * Minimal single-app dispatch + popstate wiring used when bootShell
 * auto-boots a standalone (single-entry apps map).
 *
 * Routes are sourced from the manifest's `routes` array (RegExp or string).
 * If the current path matches, the manifest's `app` factory is invoked and
 * the result is set into shell.Slot (if present) or rendered as the root.
 * Falls back to fallbackRoute when no match is found.
 *
 * This is intentionally simpler than workspace/dispatch.js — standalones
 * mount a single app and don't need a multi-app dispatch table.
 */
const _singleAppDispatch = async (shell, manifest, path) => {
  const routes = manifest.routes || []
  const matched = routes.some(r => {
    if (r instanceof RegExp) return r.test(path)
    if (typeof r === 'string') return r === path
    return false
  })
  // No route match on initial load is fine — the DOMQL router inside the
  // mounted app handles path-based page selection once the app is live.
  // We just need the app mounted; the router reads window.location itself.
  if (shell.Slot && typeof shell.Slot.set === 'function') {
    // Multi-slot shell: set the app into the Slot
    const appDef = await manifest.app?.().catch(() => null)
    if (appDef) shell.Slot.set(appDef)
  }
  // If there is no Slot, the app is already mounted as the root (create()
  // used context.app directly). Nothing more to do for dispatch.
}

/**
 * Minimal popstate wiring for single-app standalones.
 * Re-dispatches on browser back/forward.
 */
const _singleAppWireListeners = (shell, manifest) => {
  window.addEventListener('popstate', () => {
    const path = window.location.pathname + window.location.search
    _singleAppDispatch(shell, manifest, path)
  })
}

/**
 * @param {object} params
 * @param {object}   params.apps            - map of app definitions / manifests
 * @param {object}   params.defaultApp      - manifest for the default app (object or key string)
 * @param {string}   params.fallbackRoute   - route to use when dispatch fails
 * @param {object}   [params.sdk]           - pre-initialised SDK instance
 * @param {object}   [params.context]       - pre-built DOMQL context object
 * @returns {Promise<object|null>} shell element from create(), or null on auth bounce
 */
export const bootShell = async ({ apps, defaultApp, fallbackRoute, sdk, context, publicSurface = false }) => {
  // Pattern A: single-app standalone — resolve manifest factories eagerly
  // so the shell is created with a real app + context instead of an empty
  // placeholder. Multi-app callers always supply a pre-built context, so
  // this branch is only entered for standalones.
  const appKeys = Object.keys(apps || {})
  const isSingleApp = appKeys.length === 1 && !context

  let resolvedManifest = null
  if (isSingleApp) {
    resolvedManifest = apps[appKeys[0]]
    // Resolve context factory if the manifest provides one
    const manifestContext = typeof resolvedManifest.context === 'function'
      ? await resolvedManifest.context().catch(() => ({}))
      : (resolvedManifest.context || {})
    // Resolve app factory if context.app is not already set
    if (!manifestContext.app && typeof resolvedManifest.app === 'function') {
      manifestContext.app = await resolvedManifest.app().catch(() => ({}))
    }
    context = manifestContext
  }

  // Default to empty context when neither caller nor manifest provides one
  if (!context) context = {}

  // 1. Early lang/theme from localStorage
  const lang = localStorage.getItem('lang') || 'en'
  const themeMode = localStorage.getItem('themeMode') || 'auto'

  // WCAG 3.1.1 — `<html lang="...">` is required for screen readers.
  // Set as early as possible so any pre-render announce uses the right
  // pronunciation. Re-applied later when `state.lang` changes via the
  // context language-watch effect.
  if (typeof document !== 'undefined' && document.documentElement) {
    document.documentElement.lang = lang
  }

  if (context.state) {
    context.state.lang = lang
    context.state.themeMode = themeMode

    // Ensure auxiliary state fields that shared helpers depend on are
    // initialized. openNotification pushes into `notifications`; guard
    // in case the standalone state factory didn't include the field.
    if (!Array.isArray(context.state.notifications)) {
      context.state.notifications = []
    }
  }

  // Pattern B: auto-SDK instantiation.
  // Fires when no explicit `sdk` is passed AND either:
  //   (a) isSingleApp — standalone wrapper (one-entry apps map, no pre-built context), OR
  //   (b) the resolved context carries workspaceApiUrl — multi-app shell that
  //       explicitly opts into SDK wiring via its context.js export.
  //
  // This covers the workspace shell (workspace/packages/workspace/) which passes
  // 5 apps + a pre-built context, so isSingleApp=false, but still needs the SDK
  // to authenticate, fetch scopes, and expose el.sdk to sub-app components.
  // The multi-app explicit-sdk path is preserved: pass `sdk` param explicitly to
  // skip this block entirely (e.g. tests, custom shells).
  const needsSdkInit = isSingleApp || (!sdk && context?.workspaceApiUrl)
  if (!sdk && needsSdkInit) {
    try {
      sdk = getSDK()
    } catch (err) {
      console.warn('[bootShell] SDK init failed:', err?.message || err)
    }
  }

  // 4. Register sdk fetch adapter (proxy passes calls through; safe pre-init).
  if (sdk) {
    registerSdkAdapter({ sdk })
  }

  // 6. Auth gate — public auth routes must render to unauthed users
  // MUST await SDK init before reading service-proxied methods: the SDK
  // populates flat method proxies (sdk.getMe, sdk.login, …) inside
  // _createServiceProxies which only runs during initialize(). Without
  // this await, sdk.getMe() can be undefined → TypeError → bounce to
  // /login even though localStorage tokens are valid. (Cookies are
  // dropped on .localhost by Chrome — localStorage is the source of
  // truth in dev.)
  const PUBLIC_AUTH_ROUTE = /^\/(login|signin|signup|signup-confirmation|reset-password|forgot-password|verify-email)(\/|$)/
  if (sdk) {
    await awaitSDKInit()
    // Attach SDK to context.methods AFTER init so DOMQL exposes el.sdk only
    // when service proxies (sdk.getMe, sdk.login, …) are populated. Reactive
    // factories that read el.sdk on first render won't see a half-built proxy.
    if (!context.methods) context.methods = {}
    if (typeof context.methods !== 'object') context.methods = {}
    context.methods.sdk = sdk

    // Wire realtime provider — SDK exposes a transport-agnostic
    // `workspaceProject.realtime.*` API; the provider plugs in the
    // governance Supabase realtime channel as the actual wire. When
    // backend ships native socket.io realtime alongside CollabService,
    // swap the provider here without touching any consumer.
    try {
      const wp = sdk.getService?.('workspaceProject')
      if (wp && typeof wp.setRealtimeProvider === 'function') {
        wp.setRealtimeProvider(supabaseRealtimeProvider)
      }
    } catch (err) {
      console.warn('[bootShell] could not wire realtime provider:', err?.message || err)
    }

    // Auth diagnostic — one snapshot on boot so the cause of any /workspace/*
    // 401s is obvious in the console without paste-and-decode gymnastics.
    // Also exposed as window.__auth.diagnose() for on-demand re-checks.
    try {
      const dx = diagnoseAuth()
      if (typeof window !== 'undefined') {
        window.__auth = window.__auth || {}
        window.__auth.diagnose = runAuthDiagnostic
        window.__auth.snapshot = dx
      }
      // `all_missing` on cold load is a false positive — Supabase auth-js
      // hydrates `sb-*-auth-token` asynchronously after the page bootstraps.
      // Defer the warning by a tick and re-check; only log if the state
      // actually persists. Other diagnoses (sdk_only, governance_expired,
      // missing_claim) reflect real broken half-states and log immediately.
      if (dx.diagnosis === 'all_missing') {
        setTimeout(() => {
          const dx2 = diagnoseAuth()
          if (typeof window !== 'undefined') window.__auth.snapshot = dx2
          if (dx2.diagnosis !== 'ok' && dx2.diagnosis !== 'all_missing') {
            console.warn(
              `[bootShell] auth diagnostic: ${dx2.diagnosis} — ${dx2.suggestedAction}`,
              dx2.governance,
            )
          } else if (dx2.diagnosis === 'all_missing') {
            console.warn(
              `[bootShell] auth diagnostic: ${dx2.diagnosis} — ${dx2.suggestedAction}`,
              dx2.governance,
            )
          }
        }, 250)
      } else if (dx.diagnosis !== 'ok') {
        console.warn(
          `[bootShell] auth diagnostic: ${dx.diagnosis} — ${dx.suggestedAction}`,
          dx.governance,
        )
      }
      // Auto-recover from broken half-login states. SDK token without a
      // governance JWT means every /workspace/* call 401s — clear the
      // stale tokens so the auth gate redirects to /login and a fresh
      // loginAll re-mints both. Skips if already on /login to avoid loops.
      if (typeof window !== 'undefined'
          && (dx.diagnosis === 'sdk_only' || dx.diagnosis === 'governance_expired')
          && !/^\/(login|signin|signup|reset-password|forgot-password)/.test(window.location.pathname)) {
        try { sdk.logout?.() } catch (_) {}
        try {
          const supaRefs = Object.keys(localStorage).filter(k => k.startsWith('sb-') || k.startsWith('symbols_'))
          for (const k of supaRefs) localStorage.removeItem(k)
        } catch (_) {}
        const next = encodeURIComponent(window.location.pathname + window.location.search)
        window.location.replace(`/login?next=${next}`)
        return null
      }
    } catch (err) {
      console.warn('[bootShell] auth diagnostic failed:', err?.message || err)
    }
    // SDK proxies service methods directly onto the SDK instance via
    // _createServiceProxies — there is no `sdk.auth` namespace. AuthService
    // method `getMe` is exposed as flat `sdk.getMe()`. (See
    // sdk/src/utils/services.js SERVICE_METHODS map: getMe → 'auth'.)
    //
    // Skip the network round-trip when no token exists in localStorage —
    // we already know the answer is "not authed", and firing the request
    // surfaces a misleading 401 (Unauthorized) on the landing page for
    // logged-out users.
    let me = null
    const _hasSdkToken = typeof window !== 'undefined' && !!(
      window.localStorage.getItem('symbols_access_token') ||
      window.localStorage.getItem('symbols_bridge_access_token')
    )
    if (_hasSdkToken) {
      try {
        me = await sdk.getMe()
      } catch (e) {
        console.warn('[bootShell] auth gate getMe failed:', e?.message)
      }
    }
    if (!me) {
      // `publicSurface: true` opts out of the auth bounce — used by the
      // preview app, which serves visibility-gated PUBLIC snapshots and
      // must render for logged-out users. The fetch path inside preview
      // surfaces its own "not public" UI when the project gate denies.
      if (publicSurface) {
        if (context.state) context.state.user = null
        // Fall through to the rest of bootShell so the shell mounts;
        // skip the eager scope fetch since there's no user.
      } else {
        if (!PUBLIC_AUTH_ROUTE.test(window.location.pathname)) {
          window.history.replaceState(null, '', '/login')
        }
        return null
      }
    } else if (context.state) {
      context.state.user = me
    }
  }

  // 7. Eager scope fetch — ORG + WORKSPACE.
  // SDK exposes service methods FLAT (sdk.getOrganization, sdk.getWorkspace);
  // the previous sdk.organization?.get?.() chain was dead code — optional
  // chaining short-circuited on the missing namespace, returning undefined
  // and silently leaving state.org/workspace null. That null forced sub-apps
  // to refetch on render, which is one of the storm root causes.
  // workspaceProject has no get-by-id flat method (the service exposes
  // nested namespaces — tickets, chat, etc.) so we no longer attempt to
  // hydrate state.workspaceProject here.
  // Skip the eager scope fetch when running as a public surface (no
  // user) — the org/workspace lookups would 401 and waste a round-trip.
  if (sdk && context.state && context.state.user) {
    const activeOrgId = localStorage.getItem('activeOrg')
    const activeWorkspaceId = localStorage.getItem('activeWorkspace')

    const scopeFetches = []

    if (activeOrgId && typeof sdk.getOrganization === 'function') {
      scopeFetches.push(
        Promise.resolve(sdk.getOrganization(activeOrgId))
          .then(res => { context.state.org = res?.data || res || null })
          .catch(() => null)
      )
    }

    if (activeWorkspaceId && typeof sdk.getWorkspace === 'function') {
      scopeFetches.push(
        Promise.resolve(sdk.getWorkspace(activeWorkspaceId))
          .then(res => { context.state.workspace = res?.data || res || null })
          .catch(() => null)
      )
    }

    await Promise.all(scopeFetches)
  }

  // 8. Mount shell
  // The context.app is the shell DOMQL root component (app.js default export).
  // If context.app is not set, fall back to a minimal empty object so
  // bootShell is still usable in tests without a full app definition.
  // Pass the project's themeMode so smbls' resolveAndApplyTheme uses our
  // storage key — otherwise the framework default ('symbols.theme') and our
  // 'themeMode' key drift, leaving `data-theme` stuck on whatever was last
  // forced even when the user has reset to 'auto'. Setting `globalTheme`
  // explicitly here also ensures the framework removes `data-theme` when
  // mode is 'auto' so prefers-color-scheme takes over live (per CLAUDE.md
  // global-theme contract).
  const { create } = await import('smbls')
  const shellApp = context.app || {}
  const shell = create(shellApp, {
    ...context,
    globalTheme: themeMode,
    themeStorageKey: 'themeMode'
  })

  // 9-10. Pattern A: single-app auto-dispatch + listener wiring.
  // Multi-app callers (workspace/index.js) handle steps 9-10 themselves
  // by calling dispatchUrl + wireGlobalListeners after bootShell returns.
  if (isSingleApp && resolvedManifest) {
    const path = window.location.pathname + window.location.search
    await _singleAppDispatch(shell, resolvedManifest, path)
    _singleAppWireListeners(shell, resolvedManifest)
  }

  return shell
}
